<template>
  <v-card
    :disabled="configurator.accessoryLoading"
    :loading="configurator.accessoryLoading"
    width="284"
    class="sigfloCard sigfloNeutralBorder200 elevation-0 mb-2 accessoryCard"
    :class="getClass"
  >
    <external-product-dialog
      :url="accessory.detailsSource"
      :showDialog="showDetailsDialog"
      @close="showDetailsDialog = false"
    ></external-product-dialog>
    <v-chip
      rounded
      small
      v-show="accessory.required && !isDeletable && !accessory.customerSupplied"
      color="red"
      class="white--text requiredChip text-uppercase"
      >Required</v-chip
    >
    <v-chip
      outlined
      rounded
      small
      v-show="!accessory.required && !isDeletable"
      color="grey"
      class="textMuted requiredChip"
      >Optional</v-chip
    >
    <v-btn
      v-show="accessory.required && !isDeletable && !accessory.customerSupplied"
      text
      rounded
      small
      color="#B3B3B3"
      @click="skipAccessory(true)"
      class="sigfloButtonSmall600 skipButton text-capitalize"
      >I've got this!<v-icon>mdi-chevron-right</v-icon></v-btn
    >
    <v-chip rounded @click="skipAccessory(false)" v-show="accessory.customerSupplied" color="#32FF98" class="skipButton"
      >I've got this!<v-icon>mdi-close</v-icon></v-chip
    >
    <div class="pt-10">
      <div class="pa-6">
        <div class="accessoryField textDefault sigfloBodySubline1 pb-2">
          {{ accessory.name }}
        </div>
        <div class="accessoryField textMuted sigfloBodySmall pb-2">
          <v-tooltip right max-width="500">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">{{ accessory.description }}</span>
            </template>
            <span v-html="accessory.description"></span>
          </v-tooltip>
        </div>
        <div class="d-flex text-center">
          <!-- <v-img class="accessoryImage" :src="accessoryImage" contain width="110" max-height="110" /> -->
          <product-image-carousel
            :product="accessory"
            :allowCarouselDialog="true"
            :carouselHeight="110"
            :carouselWidth="110"
          />
        </div>
        <div class="detailsButton">
          <v-btn
            text
            rounded
            small
            color="#999999"
            class="sigfloButtonSmall400 text-capitalize mt-4"
            @click="showDetails"
            v-show="accessory.detailsSource"
            ><v-icon class="mr-1">mdi-information-outline</v-icon>View Details</v-btn
          >
        </div>
        <v-form ref="accessoryForm" submit-prevent>
          <div
            class="accessoryContainer pt-2"
            v-for="option in accessory.productSettingModels"
            v-bind:key="option.id"
            v-show="accessory.selected && !accessory.customerSupplied"
          >
            <div v-if="option.controlType === 'INT'">
              <v-text-field
                solo
                filled
                flat
                dense
                single-line
                type="number"
                persistent-hint
                :rules="validation.numeric"
                :suffix="option.valueSuffix"
                :label="option.name"
                value=""
                :hint="option.description"
              ></v-text-field>
            </div>
            <div v-if="option.controlType === 'DECIMAL'">
              <v-text-field
                solo
                filled
                flat
                dense
                single-line
                type="number"
                persistent-hint
                :rules="validation.numeric"
                :suffix="option.valueSuffix"
                :label="option.name"
                value=""
                :hint="option.description"
              ></v-text-field>
            </div>
            <div v-if="option.controlType === 'DropDownList' || option.controlType.startsWith('Multi')">
              <v-select
                @change="updateAccessory"
                solo
                filled
                flat
                dense
                single-line
                v-model="option.value"
                persistent-hint
                :hint="option.description"
                :items="option.productSettingValueModels"
                item-text="label"
                item-value="value"
                :label="option.name"
                :rules="validation.selection"
              >
                <template v-slot:append-outer>
                  <v-icon :color="getSettingIconColor(option)">{{ getSettingIcon(option) }}</v-icon>
                </template>
              </v-select>
            </div>
            <div v-if="option.controlType.toLowerCase() === 'yesno'">
              <v-checkbox v-model="option.value" @change="updateAccessory"></v-checkbox>
            </div>
          </div>
        </v-form>
        <div class="accessoryDescription" v-show="accessory.specifications">
          <v-tooltip right max-width="500">
            <template v-slot:activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on" v-html="accessory.specifications"></span>
            </template>
            <span v-html="accessory.specifications"></span>
          </v-tooltip>
        </div>
        <div class="d-flex pt-6" v-if="!accessory.customerSupplied">
          <div class="sigfloBodyLarge textEmphasis pt-1" v-if="!accessoryHasPriceRange(accessory)">
            ${{ formatPrice(accessory.sellPrice) }}
          </div>
          <div class="sigfloBodyLarge textEmphasis pt-1" v-if="accessoryHasPriceRange(accessory)">
            {{ getPriceRange(accessory) }}
          </div>
          <v-spacer />
          <div class="">
            <div>
              <accessory-quantity-select-button
                :accessory="accessory"
                :parentQuantity="parentQuantity"
                :productId="productId"
                @deleteAccessory="deleteAccessory"
                :duplicated="isDeletable"
                :index="index"
              />
            </div>
          </div>
        </div>
        <div class="d-flex text-right" v-if="!accessory.customerSupplied">
          <v-btn
            v-show="isDeletable"
            text
            rounded
            color="#999999"
            @click="deleteAccessory"
            class="sigfloButtonSmall400 text-capitalize mt-4"
            ><v-icon class="ml-1">mdi-delete-outline</v-icon></v-btn
          >
          <v-spacer />
          <v-btn
            v-show="canCopy"
            text
            rounded
            small
            color="#7C40DC"
            @click="duplicateAccessory"
            class="sigfloButtonSmall400 text-capitalize mt-4"
            >Add Another<v-icon class="ml-1">mdi-content-copy</v-icon></v-btn
          >
        </div>
      </div>
    </div>
  </v-card>
</template>

<script>
  import { mapState } from 'vuex';
  import accessoryQuantitySelectButton from './AccessoryQuantitySelectButton.vue';
  import externalProductDialog from '../../shared/ExternalProductDialog.vue';
  import productImageCarousel from './ProductImageCarousel.vue';

  export default {
    components: { accessoryQuantitySelectButton, externalProductDialog, productImageCarousel },
    props: {
      accessory: {},
      productId: Number,
      index: Number,
      parentQuantity: Number
    },
    data() {
      return {
        showDetailsDialog: false
      };
    },
    mounted() {
      // if (this.accessory.minQuantity === 1 && this.accessory.maxQuantity === 1 && !this.accessory.selected)
      //   this.$store.commit('configurator/selectAccessory', { selected: true, productId: this.productId, accessoryId: this.accessory.id })
      // if (this.accessory.required > 0)
      //   this.$store.commit('configurator/selectAccessory', { selected: true, productId: this.productId, accessoryId: this.accessory.id })
    },
    created() {
      this.$eventBus.$on('component-step-navigation', () => {
        if (this.$refs.accessoryForm) {
          this.$refs.accessoryForm.validate();
        }
      });
    },
    methods: {
      /**
       * Returns the setting icon color based on the option value
       *
       * @param {*} option
       */
      getSettingIconColor(option) {
        if (option.value) return '#32ff98';
        else return '#F44336';
      },
      /**
       * Returns the setting icon based on the option value
       *
       * @param {*} option
       */
      getSettingIcon(option) {
        if (option.value) return 'mdi-check-circle';
        else return 'mdi-alert';
      },
      /**
       * Opens the details url of the product in an iframe
       */
      showDetails: function () {
        this.showDetailsDialog = true;
      },
      /**
       * Validates the form
       */
      validateForm() {
        if (this.$refs && this.$refs.accessoryForm) return this.$refs.accessoryForm.validate();
        else return true;
      },
      /**
       * Deletes this accessory
       */
      deleteAccessory: function () {
        this.$store.commit('configurator/deleteAccessory', this.index);
      },
      /**
       * Duplicates this accessory
       */
      duplicateAccessory: function () {
        this.$store.dispatch('configurator/duplicateAccessory', {
          accessory: this.accessory,
          index: this.index
        });
      },
      /**
       * Marks the accessory as customer supplied and updates the accessory.
       */
      confirmSkipAccessory: function () {
        const message =
          'Please confirm you will provide this product for your system. Sig will assume you have it while moving forward!';
        this.showPlainDialog({
          display: 'Now',
          message: message,
          messageTitle: 'Confirm',
          parentId: 0,
          parentType: '',
          published: true,
          buttonArray: [
            {
              label: 'Ive got it!',
              callback: this.skipAccessory,
              icon: 'mdi-thumb-up-outline',
              color: '#7C40DC'
            },
            {
              label: 'Cancel',
              callback: this.closePlainDialog,
              icon: '',
              color: 'grey'
            }
          ]
        });
      },
      /**
       * Marks the accessory as customer supplied and updates the accessory.
       */
      skipAccessory: function (skip) {
        if (skip == null) skip = true;
        this.accessory.customerSupplied = skip;
        if (skip) this.accessory.selected = true;
        else this.accessory.selected = false;
      },
      /**
       * Updates values for this accessory
       */
      updateAccessory: function () {
        const _this = this;
        this.$store.dispatch('configurator/configureProductAccessory', {
          accessory: _this.accessory,
          index: _this.index
        });
      }
    },
    computed: {
      ...mapState(['configurator']),
      /**
       * Returns dynamic class for this card
       */
      getClass: function () {
        let classString = '';
        if (this.accessory.selected) classString += 'selectedAccessory';
        classString += this.hasRequirementError ? ' redBorder' : '';
        return classString;
      },
      /**
       * TRUE if this is a required accessory that is not selected
       */
      hasRequirementError() {
        return (
          (this.accessory.required && !this.accessory.selected && !this.isDeletable) ||
          (this.accessory.selected && !this.validateForm())
        );
      },
      /**
       * color for sig based off of required/selection status
       */
      sigColor() {
        return this.hasRequirementError ? 'red lighten-2' : 'green';
      },
      /**
       * Can this accessory be copied?
       */
      canCopy: {
        get() {
          return this.accessory.configuredProductId && this.accessory.selected;
        }
      },
      /**
       * Is this a duplicate accessory?
       * (If the previous accessory in the accesory array has the same productId, this is a duplicate and therefore deletable)
       */
      isDeletable: {
        get() {
          if (this.index == 0) return false;
          const currentProduct = this.configurator.currentSubsystemStepComponent.productModels.find(
            (x) => x.id == this.productId
          );
          const accessoryToDelete = currentProduct.productAccessoryModels[this.index];
          const previousAccessory = currentProduct.productAccessoryModels[this.index - 1];
          if (accessoryToDelete.productId == previousAccessory.productId) {
            return true;
          } else {
            return false;
          }
        }
      },
      accessoryImage: {
        set() {},
        get() {
          return this.accessory.productImageModels && this.accessory.productImageModels.length > 0
            ? this.accessory.productImageModels[0].url
            : '';
        }
      }
    }
  };
</script>

<style scoped>
  .v-card--link:focus:before {
    opacity: 0 !important;
  }
  .accessoryCard {
    opacity: 0.7;
  }
  .qtyField {
    max-width: 80px;
  }
  .requiredChip {
    position: absolute;
    left: 15px;
    top: 15px;
    z-index: 2;
  }
  .skipButton {
    position: absolute;
    right: 15px;
    top: 15px;
    z-index: 2;
  }
  .accessoryField {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 235px;
  }
  .v-card.selectedAccessory {
    border: 1px solid #8953df !important;
    opacity: 1 !important;
  }
  .redBorder {
    border: 2px solid red !important;
  }
  .detailsButton {
    margin-left: -12px;
  }
  .accessoryDescription {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    line-height: 1.5; /* Adjust line height as needed */
    height: 40px;
    margin-bottom: 2px;
  }
</style>
